import "./Mention.css";
import { dispatchProfileMentionPanel } from "./profilePanelEvents";
import { resolveMentionType } from "./mentionUtils";
import { GVTSymbol } from "@ui/typography/GVTSymbol";
import { mentionUrl } from "@ui/organisms/QuestionBox/mentionUrl";
import { onMount, Show } from "solid-js";
import { installMentionClipboard } from "../utils/mentionClipboard";

export type MentionData = {
	id?: string;
	mention: string;
	handle: string;
	mention_type?: "influencer" | "social_account";
	name?: string;
	bio?: string;
	profile_url?: string;
	profile_picture_url?: string;
	follower_count?: number;
	is_following?: boolean;
	all_follower_counts?: Array<{
		platform: string;
		platform_id?: string;
		follower_count: number;
	}>;
	platform?: string;
	view_count?: number;
	like_count?: number;
	video_count?: number;
};

export type MentionProps = {
	mention: MentionData;
	appEmbedded?: boolean;
	theme?: "blue" | "white";
};

const openProfilePanel = (mention: MentionData) => {
	dispatchProfileMentionPanel(mention);
};

export const Mention = (props: MentionProps) => {
	// Copying a mention writes the link below in the QuestionBox's own form, so
	// that a mention survives being pasted into another app (see
	// mentionClipboard).
	onMount(installMentionClipboard);

	const mentionType = () => resolveMentionType(props.mention);

	/**
	 * The account's `/mention` link, which is what makes a mention copied out of
	 * an answer paste back into the QuestionBox as a resolved chip rather than as
	 * bare `@handle` text: the clipboard's HTML flavour keeps an anchor's `href`,
	 * and the QuestionBox reads its payload straight back (see mentionUrl). Null
	 * for a mention with no public id, which cannot be addressed by a URL.
	 */
	const mentionHref = () => {
		const type = mentionType();
		if (!type) return null;
		return mentionUrl({
			kind: "mention",
			data: {
				type,
				handle: props.mention.handle,
				// Only a channel is platform-specific; an influencer stands for its
				// handle everywhere, matching the chips the context selector inserts.
				platform:
					type === "social_account" ? props.mention.platform : undefined,
				id: props.mention.id,
			},
		});
	};

	const MentionLabel = () => (
		<>
			<GVTSymbol>{props.mention.platform ? `${props.mention.platform}:` : "@"}</GVTSymbol>
			{props.mention.handle}
		</>
	);

	const mentionContent = () => (
		<Show
			when={mentionHref()}
			fallback={
				<span class="mention-label">
					<MentionLabel />
				</span>
			}
		>
			{(href) => (
				// Not a tab stop of its own: the chip around it is already the thing
				// you focus and activate, and the link is here to be copied, not
				// followed — clicking still opens the profile panel.
				<a
					class="mention-label"
					href={href()}
					tabIndex={-1}
					draggable={false}
					onClick={(event) => event.preventDefault()}
				>
					<MentionLabel />
				</a>
			)}
		</Show>
	);
	if (!mentionType()) {
		return (
			<span class="mention no-details" classList={{ "mention--white": props.theme === "white" }}>
				{mentionContent()}
			</span>
		);
	} else if (props.appEmbedded) {
		return (
			<span
				class="mention"
				classList={{ "mention--white": props.theme === "white" }}
				data-handle={props.mention.handle}
				data-type="social_account"
				data-platform={props.mention.platform}
				onPointerDown={(e) => e.preventDefault()}
				onPointerUp={() => {
					document.dispatchEvent(
						new CustomEvent("mentiontapped", { detail: props.mention }),
					);
				}}
			>
				{mentionContent()}
			</span>
		);
	} else {
		return (
			<span
				class="mention"
				classList={{ "mention--white": props.theme === "white" }}
				data-handle={props.mention.handle}
				data-type={mentionType()}
				data-platform={props.mention.platform}
				data-testid="chat-mention"
				role="button"
				tabIndex={0}
				onClick={() => openProfilePanel(props.mention)}
				onKeyDown={(event) => {
					if (event.key === "Enter" || event.key === " ") {
						event.preventDefault();
						openProfilePanel(props.mention);
					}
				}}
			>
				{mentionContent()}
			</span>
		);
	}
};
